import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Link,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { useProjectDetails } from '../context/ProjectDetailsContext';
import type { ProjectPluginSummaryType } from '@/generated/graphql';

type PluginType = 'VST2' | 'VST3' | 'AU' | 'CLAP' | 'unknown';

const PLUGIN_TYPE_COLORS: Record<
  PluginType,
  'primary' | 'secondary' | 'success' | 'warning' | 'default'
> = {
  VST2: 'primary',
  VST3: 'secondary',
  AU: 'success',
  CLAP: 'warning',
  unknown: 'default',
};

function confidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'success.main';
  if (confidence >= 0.5) return 'warning.main';
  return 'text.secondary';
}

function formatConfidencePct(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

const PluginsTab: React.FC = () => {
  const { project, selectedVersion } = useProjectDetails();

  // Get plugins from the selected version
  const plugins = useMemo<ProjectPluginSummaryType[]>(() => {
    if (!project?.versionHistory?.length) return [];
    const version = project.versionHistory.find(
      (v) => v.version === selectedVersion
    );
    return version?.plugins ?? [];
  }, [project, selectedVersion]);

  // Group by pluginType
  const grouped = useMemo(() => {
    const order: PluginType[] = ['VST3', 'VST2', 'AU', 'CLAP', 'unknown'];
    const map = new Map<string, ProjectPluginSummaryType[]>();
    for (const plugin of plugins) {
      const pt = (plugin.pluginType as PluginType | undefined) ?? 'unknown';
      const arr = map.get(pt) ?? [];
      arr.push(plugin);
      map.set(pt, arr);
    }
    // Sort each group by confidence desc
    for (const arr of map.values()) {
      arr.sort((a, b) => b.confidence - a.confidence);
    }
    // Return groups in defined order, then any remaining
    const result: Array<{
      type: PluginType;
      items: ProjectPluginSummaryType[];
    }> = [];
    for (const type of order) {
      const items = map.get(type);
      if (items?.length) {
        result.push({ type, items });
        map.delete(type);
      }
    }
    for (const [type, items] of map) {
      if (items.length) result.push({ type: type as PluginType, items });
    }
    return result;
  }, [plugins]);

  const alphaBanner = (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <strong>Alpha feature</strong> — Plugin detection is VERY experimental and
      may produce inaccurate results. Found an issue? Report it in the{' '}
      <Link
        href="https://discord.gg/srd3rH6WYU"
        target="_blank"
        rel="noopener noreferrer"
      >
        community Discord
      </Link>
      . Any feedback will help us improve the detection algorithms and build out
      better support for different DAWs and plugin formats!
    </Alert>
  );

  if (!plugins.length) {
    return (
      <Box>
        {alphaBanner}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={160}
          gap={1}
          color="text.secondary"
        >
          <Typography variant="body1">No plugins detected yet.</Typography>
          <Typography variant="body2">
            Create a new version from a DAW project file (.als, .rpp,
            .bwproject, .song, .flp…) to detect plugins.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {alphaBanner}
      {grouped.map(({ type, items }, groupIdx) => (
        <Box key={type} mb={2}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ px: 1, mb: 0.5 }}
          >
            {type === 'unknown' ? 'Unidentified Format' : type}
          </Typography>
          <List dense disablePadding>
            {items.map((plugin, idx) => (
              <React.Fragment key={`${type}-${plugin.name}-${idx}`}>
                <ListItem alignItems="flex-start" sx={{ py: 0.75 }}>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {plugin.name}
                        </Typography>
                        {plugin.vendor && (
                          <Typography variant="caption" color="text.secondary">
                            {plugin.vendor}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                        {plugin.pluginType &&
                          plugin.pluginType !== 'unknown' && (
                            <Chip
                              label={plugin.pluginType}
                              size="small"
                              color={
                                PLUGIN_TYPE_COLORS[
                                  plugin.pluginType as PluginType
                                ] ?? 'default'
                              }
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          )}
                        {plugin.vst2Id && (
                          <Chip
                            label={`ID: ${plugin.vst2Id}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                        <Chip
                          label={`${plugin.detectionCount}× detected`}
                          size="small"
                          variant="filled"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: confidenceColor(plugin.confidence),
                            alignSelf: 'center',
                          }}
                        >
                          {formatConfidencePct(plugin.confidence)} confidence
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {idx < items.length - 1 && (
                  <Divider component="li" variant="inset" />
                )}
              </React.Fragment>
            ))}
          </List>
          {groupIdx < grouped.length - 1 && <Divider sx={{ mt: 1 }} />}
        </Box>
      ))}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1 }}
      >
        {plugins.length} unique plugin{plugins.length !== 1 ? 's' : ''} detected
        in version {selectedVersion}
      </Typography>
    </Box>
  );
};

export default PluginsTab;
